import { useMemo, useState } from 'react'
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react'
import Pager from '../components/Pager.jsx'
import { THAI_COLLATOR, fmtNumber } from './format.js'
import { ErrorState, Loading, EmptyState, NoMatchState } from './States.jsx'

/**
 * <DataTable> — every list of records. Audit: 84 hand-built tables, 30 header
 * styles, sorting in 8 (each different), paging missing on 28, and 18 clickable
 * rows with no pointer cursor.
 *
 * columns: [{
 *   key         field name (also the default sort value and cell text)
 *   label       header text
 *   render      (row) => node            — custom cell
 *   sortable    true → click the header to sort
 *   sortValue   (row) => string|number   — what to sort by when it differs from the cell
 *   align       'left' (default) | 'right' (money, counts — always right) | 'center'
 *   width       e.g. '120px' or '20%'
 *   hideBelow   'sm' | 'md' | 'lg'       — drop the column on small screens
 *   defaultDir  first-click direction: 'asc' (text) | 'desc' (dates, money)
 * }]
 *
 * rows          the CURRENT PAGE of rows (or all rows for client paging — see useTableState)
 * rowKey        (row) => key
 * onRowClick    (row) => void — row gets pointer, hover, Enter/Space
 * sort, onSortChange   { key, dir } controlled sort (see useTableState)
 * loading       true on first load → spinner; true with rows already shown → rows dim, no blink
 * error, onRetry       error replaces the body; never shown together with "empty"
 * filtered, onClearFilters   empty + filtered → "ไม่พบรายการที่ตรงกับตัวกรอง" + ล้างตัวกรองทั้งหมด
 * empty         { title, hint, action } for the not-filtered empty state
 * page, pageTotal, onPageChange, total  → footer "ทั้งหมด N รายการ" + <Pager> (bottom, always)
 *
 * groupBy       (row) => key — draws a group heading row each time the key changes
 *               down the list (sort by it yourself: this renders groups, it does not form them)
 * groupHeader   (key, rowsInGroup) => node — the heading's content (default: the key)
 * rowAccent     (row) => CSS colour | null — a 3px bar down the row's left edge
 *               (e.g. appointment status colour); full row height
 * rowClassName  (row) => extra classes for the <tr> (e.g. a "last opened" tint)
 * fixed         true → table-fixed layout, so `width` on columns is exact and two
 *               tables with the same columns line up
 * valign        'middle' (default) | 'top' — for tables whose cells stack 2-4 lines
 *               (a fact with its detail under it): top keeps every column's FIRST line
 *               on one line, so a tall cell no longer pushes the rest of the row to its
 *               middle. One-line tables look the same either way, so the default stays.
 */
const HIDE = { sm: 'hidden sm:table-cell', md: 'hidden md:table-cell', lg: 'hidden lg:table-cell' }
const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' }

export function DataTable({
  columns, rows, rowKey = (r) => r.id, onRowClick,
  sort, onSortChange,
  loading = false, error, onRetry,
  filtered = false, onClearFilters, empty,
  page, pageTotal, onPageChange, total,
  groupBy, groupHeader, rowAccent, rowClassName, fixed = false, valign = 'middle',
  minWidth, className = '',
}) {
  const list = Array.isArray(rows) ? rows : []
  const firstLoad = loading && list.length === 0
  const colSpan = columns.length

  const toggleSort = (c) => {
    if (!onSortChange || !c.sortable) return
    if (sort?.key === c.key) onSortChange({ key: c.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' })
    else onSortChange({ key: c.key, dir: c.defaultDir || 'asc' })
  }

  let body
  if (error) {
    body = <tr><td colSpan={colSpan} className="p-4"><ErrorState message={typeof error === 'string' ? error : undefined} onRetry={onRetry} /></td></tr>
  } else if (firstLoad) {
    body = <tr><td colSpan={colSpan}><Loading /></td></tr>
  } else if (list.length === 0) {
    body = (
      <tr><td colSpan={colSpan}>
        {filtered
          ? <NoMatchState onClear={onClearFilters} />
          : <EmptyState variant="plain" title={empty?.title || 'ยังไม่มีรายการ'} hint={empty?.hint} action={empty?.action} />}
      </td></tr>
    )
  } else {
    const groupSizes = new Map()
    if (groupBy) for (const r of list) { const k = groupBy(r); groupSizes.set(k, (groupSizes.get(k) || 0) + 1) }
    body = list.flatMap((r, i) => {
      const click = onRowClick ? {
        onClick: () => onRowClick(r),
        // Only when the ROW itself has focus: Enter/Space on a checkbox, link or
        // dropdown inside the row must do that control's job, not open the row.
        onKeyDown: (e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(r) } },
        tabIndex: 0,
        className: 'cursor-pointer hover:bg-slate-50/70 focus-visible:outline-none focus-visible:bg-brand-50/50',
      } : { className: '' }
      const accent = rowAccent ? rowAccent(r) : null
      const out = []
      if (groupBy) {
        const k = groupBy(r)
        if (i === 0 || groupBy(list[i - 1]) !== k) {
          const groupRows = list.filter((x) => groupBy(x) === k)
          out.push(
            <tr key={`g:${String(k)}:${i}`} className="bg-slate-50/80 border-b border-slate-100">
              <td colSpan={colSpan} className="px-4 py-1.5 text-[12.5px] font-semibold text-slate-600">
                {groupHeader ? groupHeader(k, groupRows) : <>{String(k)} <span className="font-normal text-slate-400 tabular-nums">· {groupSizes.get(k)}</span></>}
              </td>
            </tr>,
          )
        }
      }
      out.push(
        <tr key={rowKey(r, i)} {...click} className={`border-b border-slate-100 last:border-0 transition-colors ${click.className} ${rowClassName ? rowClassName(r) || '' : ''}`}>
          {columns.map((c, ci) => (
            <td key={c.key} style={ci === 0 && accent ? { boxShadow: `inset 3px 0 0 ${accent}` } : undefined}
              className={`px-4 py-2.5 ${valign === 'top' ? 'align-top' : 'align-middle'} text-[13.5px] text-slate-700 ${ALIGN[c.align] || ''} ${c.align === 'right' ? 'tabular-nums' : ''} ${HIDE[c.hideBelow] || ''} ${c.className || ''}`}>
              {c.render ? c.render(r) : (r[c.key] ?? '—')}
            </td>
          ))}
        </tr>,
      )
      return out
    })
  }

  // An empty page that is not the only page keeps its pager: rows can be filtered out of
  // ONE page on the client (the implant call list hides rows a colleague holds), and the
  // way on is the next page.
  const showFooter = !error && !firstLoad && (list.length > 0 || (pageTotal || 0) > 1) && (total != null || (pageTotal || 0) > 1)

  return (
    <div className={`rounded-2xl border border-slate-200 bg-white overflow-hidden ${className}`}>
      <div className="overflow-x-auto">
        <table className={`w-full border-collapse transition-opacity ${fixed ? 'table-fixed' : ''} ${loading && !firstLoad ? 'opacity-60' : ''}`} style={minWidth ? { minWidth } : undefined}>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/60">
              {columns.map((c) => {
                const active = sort?.key === c.key
                const Arrow = !active ? ChevronsUpDown : sort.dir === 'asc' ? ChevronUp : ChevronDown
                const sortable = c.sortable && onSortChange
                return (
                  <th key={c.key} scope="col" style={c.width ? { width: c.width } : undefined}
                    aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                    className={`px-4 py-2.5 text-[12px] font-medium text-slate-500 whitespace-nowrap ${ALIGN[c.align] || 'text-left'} ${HIDE[c.hideBelow] || ''}`}>
                    {sortable ? (
                      <button type="button" onClick={() => toggleSort(c)}
                        className={`inline-flex items-center gap-1 hover:text-slate-800 ${active ? 'text-slate-800' : ''} ${c.align === 'right' ? 'flex-row-reverse' : ''}`}>
                        {c.label}
                        <Arrow size={12} className={active ? 'text-brand-600' : 'text-slate-300'} />
                      </button>
                    ) : c.label}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>{body}</tbody>
        </table>
      </div>
      {showFooter && (
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-4 py-2.5">
          <span className="text-[12.5px] text-slate-500">ทั้งหมด <span className="tabular-nums font-medium text-slate-700">{fmtNumber(total ?? list.length)}</span> รายการ</span>
          {onPageChange && <Pager page={page} pageTotal={pageTotal} onChange={onPageChange} disabled={loading} size="sm" />}
        </div>
      )}
    </div>
  )
}

/** Sort rows on the client with the shared Thai collator. Stable; null/'' always last. */
export function sortRows(rows, columns, sort) {
  if (!sort?.key) return rows
  const col = columns.find((c) => c.key === sort.key)
  const val = (r) => (col?.sortValue ? col.sortValue(r) : r[sort.key])
  const sign = sort.dir === 'desc' ? -1 : 1
  return rows
    .map((r, i) => ({ r, i, v: val(r) }))
    .sort((a, b) => {
      const ae = a.v == null || a.v === ''
      const be = b.v == null || b.v === ''
      if (ae || be) return ae && be ? a.i - b.i : ae ? 1 : -1
      const c = typeof a.v === 'number' && typeof b.v === 'number' ? a.v - b.v : THAI_COLLATOR.compare(String(a.v), String(b.v))
      return c ? c * sign : a.i - b.i
    })
    .map((x) => x.r)
}

/**
 * Client-side sort + paging for lists that arrive whole (≤ a few thousand rows).
 * Server-paged lists keep their own page state and pass it straight to DataTable.
 *   const t = useTableState(rows, columns, { pageSize: 50, sort: { key: 'date', dir: 'desc' } })
 *   <DataTable columns={columns} {...t.tableProps} />
 * Changing the sort goes back to page 1.
 */
export function useTableState(rows, columns, { pageSize = 50, sort: initialSort = null } = {}) {
  const [sort, setSort] = useState(initialSort)
  const [page, setPage] = useState(1)
  const all = Array.isArray(rows) ? rows : []
  const sorted = useMemo(() => sortRows(all, columns, sort), [all, columns, sort])
  const pageTotal = Math.max(1, Math.ceil(sorted.length / pageSize))
  const cur = Math.min(page, pageTotal)
  const pageRows = sorted.slice((cur - 1) * pageSize, cur * pageSize)
  return {
    sort, setSort, page: cur, setPage,
    tableProps: {
      rows: pageRows,
      sort,
      onSortChange: (s) => { setSort(s); setPage(1) },
      page: cur,
      pageTotal,
      onPageChange: setPage,
      total: sorted.length,
    },
  }
}
